import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";

import { useUserOptional } from "@/auth/UserContext";
import { usePrefetchKaiWelcomePrompts } from "@/hooks/useKaiWelcomePrompts";
import { useApplyKaiHomeLayout } from "@/hooks/useApplyKaiHomeLayout";
import { useApplyKaiPreferences } from "@/hooks/useApplyKaiPreferences";
import { useApplyKaiTableFilters } from "@/hooks/useApplyKaiTableFilters";
import { useApplyKaiFeedFilters } from "@/hooks/useApplyKaiFeedFilters";
import { useApplyKaiTablePreferences } from "@/hooks/useApplyKaiTablePreferences";
import {
  dispatchHomeLayoutReview,
  KAI_HOME_LAYOUT_RESOLVE_EVENT,
} from "@/features/dashboard/home/kaiHomeLayoutBridge";
import type { HomeLayoutReview } from "@/types/home";
import { buildUserDisplayContent } from "@/lib/kaiAttachments";
import {
  kaiApi,
  type KaiChatMessage,
  type KaiConversationSummary,
  type KaiConversationWithMessages,
  type KaiFeedEventContext,
  type KaiMailReviewContext,
} from "@/services/kaiApi";
import { type KaiEntityTag } from "./kaiEntityTags";
import { buildKaiFilterContext } from "./kaiFilterContext";
import { buildKaiFeedContext } from "./kaiFeedContext";
import { buildKaiHomeContext } from "./kaiHomeContext";
import { buildKaiTableContext } from "./kaiTableContext";
import { buildKaiBookingDetailContext } from "./kaiBookingDetailContext";
import {
  hasActiveClarification,
  normalizeClarificationFields,
} from "./clarificationFieldUtils";
import { parsePlanFromMessageMetadata } from "./kaiPlanHelpers";
import { sanitizeKaiStatusMessage } from "./kaiStatusHelpers";
import {
  type KaiFeedbackSentiment,
  type KaiMessageFeedbackState,
} from "./kaiFeedback";
import {
  formatToolArgs,
  groupKaiThread,
  updatePlanStep,
  type KaiConversation,
  type KaiMessage,
  type KaiMessageMetadata,
  type KaiPlanArtifact,
  type KaiPlanStepStatus,
  type KaiSlashSelection,
  type KaiSsePayload,
  type KaiThreadItem,
  type KaiToolActivity,
  type PendingClarification,
  type PendingElicitation,
} from "./kaiTypes";

function newId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function summaryToConversation(s: KaiConversationSummary): KaiConversation {
  return {
    id: s.id,
    title: s.title ?? "New chat",
    preview: s.message_count > 0 ? "" : "Start a conversation…",
    createdAt: s.created_at,
    updatedAt: s.updated_at,
    messages: [],
  };
}

function previewFromApiMessages(messages: KaiChatMessage[]): string {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role !== "user" && m.role !== "assistant") continue;
    const text = (m.content ?? "").trim();
    if (text) return text.slice(0, 48);
  }
  return "";
}

function previewFromLocalMessages(messages: KaiMessage[]): string {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role !== "user" && m.role !== "assistant") continue;
    const text = m.content.trim();
    if (text) return text.slice(0, 48);
  }
  return "";
}

function mapMessages(messages: KaiChatMessage[]): KaiMessage[] {
  return messages
    .filter(
      (m) => m.role === "user" || m.role === "assistant" || m.role === "tool",
    )
    .map((m) => {
      const meta = m.metadata;
      const entityTags =
        m.role === "user" && meta?.entity_tags?.length
          ? (meta.entity_tags as KaiEntityTag[])
          : undefined;
      const slashCommand =
        m.role === "user" && meta?.slash_command
          ? {
              id: meta.slash_command.id,
              label: meta.slash_command.label,
            }
          : undefined;
      return {
        id: m.id,
        role: m.role as KaiMessage["role"],
        content: m.content ?? "",
        createdAt: m.created_at,
        toolName: m.tool_name ?? undefined,
        plan:
          m.role === "assistant"
            ? parsePlanFromMessageMetadata(m.metadata)
            : null,
        entityTags,
        slashCommand,
        metadata: (m.metadata as unknown as KaiMessageMetadata | null) ?? undefined,
      };
    });
}

function conversationTitle(conv: KaiConversationWithMessages): string {
  if (conv.title) return conv.title;
  const firstUser = conv.messages.find((m) => m.role === "user");
  return firstUser?.content?.slice(0, 32) ?? "New chat";
}

interface KaiChatContextValue {
  historyOpen: boolean;
  toggleHistory: () => void;
  closeHistory: () => void;
  conversations: KaiConversation[];
  activeId: string | null;
  isGenerating: boolean;
  /** @deprecated use isGenerating */
  isTyping: boolean;
  selectConversation: (id: string) => void;
  startNewChat: () => void;
  /**
   * Seed (or clear) the active feed event so Kai fetches and reasons about it,
   * and keeps it in scope for follow-ups. Set right before `sendMessage` when
   * launching an "Ask Kai" flow from a feed row.
   */
  setFeedEventFocus: (ctx: KaiFeedEventContext | null) => void;
  /**
   * Seed (or clear) the active email the user drilled into from a mail-review
   * card, so Kai opens it with `get_graph_mail_context` and keeps it in scope.
   * Set right before `sendMessage` from a `mail_review_snapshot` row action.
   */
  setMailReviewFocus: (ctx: KaiMailReviewContext | null) => void;
  sendMessage: (
    text: string,
    entityTags?: KaiEntityTag[],
    attachmentIds?: string[],
    attachmentMeta?: KaiMessageMetadata,
    slash?: KaiSlashSelection | null,
  ) => void;
  /** Resolve/create the active conversation (used before uploading files). */
  ensureConversation: () => Promise<string | null>;
  stopGeneration: () => void;
  messages: KaiMessage[];
  thread: KaiThreadItem[];
  isWelcome: boolean;
  streamingContent: string;
  streamingArtifacts: Record<string, unknown>;
  toolActivity: KaiToolActivity[];
  mcpStatus: string;
  planProgress: KaiPlanArtifact | null;
  followUpSuggestions: string[];
  pendingClarifications: PendingClarification[];
  awaitingClarification: boolean;
  submitClarification: (
    clarificationId: string,
    answers: Record<string, string>,
  ) => void;
  pendingElicitations: PendingElicitation[];
  respondToElicitation: (
    elicitationId: string,
    action: "accept" | "decline",
  ) => void;
  /** Pending end-of-turn Home layout review from Kai (null when none). */
  homeLayoutReview: HomeLayoutReview | null;
  dismissHomeLayoutReview: () => void;
  error: string | null;
  dismissError: () => void;
  messageFeedback: KaiMessageFeedbackState;
  submitMessageFeedback: (
    messageId: string,
    sentiment: KaiFeedbackSentiment,
  ) => void;
}

const KaiChatContext = createContext<KaiChatContextValue | null>(null);

export function KaiChatProvider({ children }: { children: ReactNode }) {
  const userCtx = useUserOptional();
  const isAuthenticated = userCtx?.isAuthenticated ?? false;
  const location = useLocation();
  const routeRef = useRef(location.pathname);
  routeRef.current = location.pathname;
  const profileRef = useRef(userCtx?.profile ?? null);
  profileRef.current = userCtx?.profile ?? null;

  const applyKaiPreferences = useApplyKaiPreferences();
  const applyKaiTablePreferences = useApplyKaiTablePreferences();
  const applyKaiTableFilters = useApplyKaiTableFilters();
  const applyKaiFeedFilters = useApplyKaiFeedFilters();
  const applyKaiHomeLayout = useApplyKaiHomeLayout();
  usePrefetchKaiWelcomePrompts();

  const [historyOpen, setHistoryOpen] = useState(false);
  const [conversations, setConversations] = useState<KaiConversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [streamingArtifacts, setStreamingArtifacts] = useState<
    Record<string, unknown>
  >({});
  const [toolActivity, setToolActivity] = useState<KaiToolActivity[]>([]);
  const [mcpStatus, setMcpStatus] = useState("");
  const [planProgress, setPlanProgress] = useState<KaiPlanArtifact | null>(
    null,
  );
  const [followUpSuggestions, setFollowUpSuggestions] = useState<string[]>([]);
  const [pendingClarifications, setPendingClarifications] = useState<
    PendingClarification[]
  >([]);
  const [pendingElicitations, setPendingElicitations] = useState<
    PendingElicitation[]
  >([]);
  const [homeLayoutReview, setHomeLayoutReview] =
    useState<HomeLayoutReview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [messageFeedback, setMessageFeedback] =
    useState<KaiMessageFeedbackState>({});

  const planProgressRef = useRef<KaiPlanArtifact | null>(null);
  const feedEventFocusRef = useRef<KaiFeedEventContext | null>(null);
  const mailReviewFocusRef = useRef<KaiMailReviewContext | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const streamGenRef = useRef(0);
  const pendingClarificationsRef = useRef<PendingClarification[]>([]);
  pendingClarificationsRef.current = pendingClarifications;
  const awaitingClarification = hasActiveClarification(pendingClarifications);

  const activeConversation = conversations.find((c) => c.id === activeId);
  const messages = activeConversation?.messages ?? [];
  const thread = useMemo(() => groupKaiThread(messages), [messages]);
  const isWelcome = activeId === null && !isGenerating && messages.length === 0;

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    kaiApi
      .listConversations()
      .then((list) => {
        if (!cancelled) setConversations(list.map(summaryToConversation));
      })
      .catch((err) => console.warn("Failed to load Kai conversations", err));
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const closeHistory = useCallback(() => setHistoryOpen(false), []);
  const toggleHistory = useCallback(() => setHistoryOpen((open) => !open), []);
  const dismissError = useCallback(() => setError(null), []);
  const dismissHomeLayoutReview = useCallback(
    () => setHomeLayoutReview(null),
    [],
  );

  // Dismiss the chat review card once the Home editor resolves the session
  // (whether the user approved/undid from the card or the edit-header banner).
  useEffect(() => {
    const onResolve = () => setHomeLayoutReview(null);
    window.addEventListener(KAI_HOME_LAYOUT_RESOLVE_EVENT, onResolve);
    return () =>
      window.removeEventListener(KAI_HOME_LAYOUT_RESOLVE_EVENT, onResolve);
  }, []);

  const resetLiveState = useCallback(() => {
    setStreamingContent("");
    setStreamingArtifacts({});
    setToolActivity([]);
    setMcpStatus("");
    planProgressRef.current = null;
    setPlanProgress(null);
    setPendingClarifications([]);
    setPendingElicitations([]);
  }, []);

  const patchConversation = useCallback(
    (id: string, patch: Partial<KaiConversation>) => {
      setConversations((prev) =>
        prev.map((c) => (c.id === id ? { ...c, ...patch } : c)),
      );
    },
    [],
  );

  const setConversationMessages = useCallback(
    (id: string, updater: (msgs: KaiMessage[]) => KaiMessage[]) => {
      setConversations((prev) =>
        prev.map((c) =>
          c.id === id ? { ...c, messages: updater(c.messages) } : c,
        ),
      );
    },
    [],
  );

  const hydrateConversation = useCallback((conv: KaiConversationWithMessages) => {
    const preview = previewFromApiMessages(conv.messages);
    setConversations((prev) =>
      prev.map((c) =>
        c.id === conv.id
          ? {
              ...c,
              title: conversationTitle(conv),
              preview: preview || c.preview,
              messages: mapMessages(conv.messages),
              createdAt: conv.created_at,
              updatedAt: conv.updated_at,
            }
          : c,
      ),
    );
  }, []);

  const submitMessageFeedback = useCallback(
    (messageId: string, sentiment: KaiFeedbackSentiment) => {
      setMessageFeedback((prev) => ({ ...prev, [messageId]: sentiment }));
      const conversationId = activeId;
      if (!conversationId) return;
      kaiApi
        .submitFeedback(conversationId, messageId, sentiment)
        .catch((err) => console.warn("Failed to submit Kai feedback:", err));
    },
    [activeId],
  );

  const selectConversation = useCallback(
    (id: string) => {
      streamGenRef.current += 1;
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      setIsGenerating(false);
      resetLiveState();
      setFollowUpSuggestions([]);
      setError(null);
      setMessageFeedback({});
      setActiveId(id);
      setHistoryOpen(false);
      feedEventFocusRef.current = null;
      mailReviewFocusRef.current = null;
      kaiApi
        .getConversation(id)
        .then(hydrateConversation)
        .catch((err) => console.warn("Failed to load conversation", err));
    },
    [hydrateConversation, resetLiveState],
  );

  const startNewChat = useCallback(() => {
    streamGenRef.current += 1;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsGenerating(false);
    resetLiveState();
    setFollowUpSuggestions([]);
    setError(null);
    setMessageFeedback({});
    setActiveId(null);
    setHistoryOpen(false);
    feedEventFocusRef.current = null;
    mailReviewFocusRef.current = null;
  }, [resetLiveState]);

  const setFeedEventFocus = useCallback(
    (ctx: KaiFeedEventContext | null) => {
      feedEventFocusRef.current = ctx;
    },
    [],
  );

  const setMailReviewFocus = useCallback(
    (ctx: KaiMailReviewContext | null) => {
      mailReviewFocusRef.current = ctx;
    },
    [],
  );

  const stopGeneration = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  const submitClarification = useCallback(
    async (clarificationId: string, answers: Record<string, string>) => {
      setPendingClarifications((prev) =>
        prev.map((c) =>
          c.clarificationId === clarificationId
            ? { ...c, status: "submitting" as const }
            : c,
        ),
      );
      try {
        await kaiApi.respondToClarification(clarificationId, answers);
        setPendingClarifications((prev) =>
          prev.map((c) =>
            c.clarificationId === clarificationId
              ? { ...c, status: "resolved" as const, answers }
              : c,
          ),
        );
      } catch (err) {
        console.error("Clarification submission failed", err);
        setError(
          `Clarification submission failed: ${
            err instanceof Error ? err.message : String(err)
          }`,
        );
        setPendingClarifications((prev) =>
          prev.map((c) =>
            c.clarificationId === clarificationId
              ? { ...c, status: "pending" as const }
              : c,
          ),
        );
      }
    },
    [],
  );

  const respondToElicitation = useCallback(
    async (elicitationId: string, action: "accept" | "decline") => {
      setPendingElicitations((prev) =>
        prev.map((e) =>
          e.elicitationId === elicitationId
            ? { ...e, status: "submitting" as const }
            : e,
        ),
      );
      try {
        await kaiApi.respondToElicitation(elicitationId, action);
        setPendingElicitations((prev) =>
          prev.map((e) =>
            e.elicitationId === elicitationId
              ? {
                  ...e,
                  status: "resolved" as const,
                  result: action === "accept" ? "approved" : "rejected",
                }
              : e,
          ),
        );
      } catch (err) {
        console.error("Elicitation response failed", err);
        setPendingElicitations((prev) =>
          prev.map((e) =>
            e.elicitationId === elicitationId
              ? {
                  ...e,
                  status: "error" as const,
                  errorMessage: err instanceof Error ? err.message : String(err),
                }
              : e,
          ),
        );
      }
    },
    [],
  );

  /**
   * Resolve the active conversation, creating one on the server if needed.
   * Shared by the send path and the attachment-upload flow (files must belong
   * to a conversation before the first message is sent). Returns `null` if a
   * conversation could not be established.
   */
  const ensureConversation = useCallback(async (): Promise<string | null> => {
    if (activeId) return activeId;
    try {
      const created = await kaiApi.createConversation();
      setConversations((prev) => [
        summaryToConversation({
          id: created.id,
          title: created.title,
          model: created.model,
          created_at: created.created_at,
          updated_at: created.updated_at,
          message_count: 0,
        }),
        ...prev,
      ]);
      setActiveId(created.id);
      return created.id;
    } catch (err) {
      console.error("Failed to create Kai conversation", err);
      setError("Could not start a conversation. Please try again.");
      return null;
    }
  }, [activeId]);

  const sendMessage = useCallback(
    async (
      text: string,
      entityTags: KaiEntityTag[] = [],
      attachmentIds: string[] = [],
      attachmentMeta?: KaiMessageMetadata,
      slash: KaiSlashSelection | null = null,
    ) => {
      const trimmed = text.trim();
      if (
        !trimmed &&
        entityTags.length === 0 &&
        attachmentIds.length === 0 &&
        slash == null
      ) {
        return;
      }
      const canSupersede = hasActiveClarification(
        pendingClarificationsRef.current,
      );
      if (isGenerating && !canSupersede) return;

      if (isGenerating && canSupersede) {
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        streamGenRef.current += 1;
        setPendingClarifications([]);
      }

      const conversationId = await ensureConversation();
      if (!conversationId) return;

      const filenames = attachmentMeta?.filenames ?? [];
      const displayContent = buildUserDisplayContent(trimmed, filenames);
      // Tags are inlined in `trimmed` at their position (e.g. "…for @Acme");
      // the structured `entity_tags` payload below carries canonical values.
      const taggedContent = trimmed;
      const userMsg: KaiMessage = {
        id: newId("msg"),
        role: "user",
        content: displayContent,
        createdAt: new Date().toISOString(),
        entityTags: entityTags.length > 0 ? entityTags : undefined,
        slashCommand: slash ? { id: slash.id, label: slash.label } : undefined,
        metadata: attachmentMeta,
      };

      setConversationMessages(conversationId, (msgs) => [...msgs, userMsg]);
      patchConversation(conversationId, {
        preview: displayContent.slice(0, 48),
        updatedAt: new Date().toISOString(),
      });
      setFollowUpSuggestions([]);
      setError(null);
      resetLiveState();
      setIsGenerating(true);

      const controller = new AbortController();
      abortControllerRef.current = controller;
      const gen = ++streamGenRef.current;

      let accumulated = "";
      let committed = false;
      const collectedTools: KaiToolActivity[] = [];

      const stillCurrent = () => streamGenRef.current === gen;

      try {
        await kaiApi.streamMessage(
          conversationId,
          taggedContent,
          (event) => {
            if (!stillCurrent()) return;
            let data: KaiSsePayload = {};
            try {
              data = JSON.parse(event.data) as KaiSsePayload;
            } catch {
              return;
            }

            switch (event.type) {
              case "text_delta":
                accumulated += data.delta || "";
                setStreamingContent(accumulated);
                break;

              case "text":
                accumulated = data.content || accumulated;
                setStreamingContent(accumulated);
                break;

              case "artifact":
                if (data.kind && data.payload != null) {
                  setStreamingArtifacts((prev) => ({
                    ...prev,
                    [data.kind as string]: data.payload,
                  }));
                }
                break;

              case "tool_start": {
                const toolArgs = formatToolArgs(data.arguments);
                collectedTools.push({
                  name: data.name || "unknown",
                  status: "done",
                  args: toolArgs,
                });
                setToolActivity((prev) => [
                  ...prev.map((t) => ({ ...t, status: "done" as const })),
                  {
                    name: data.name || "unknown",
                    status: "running",
                    args: toolArgs,
                  },
                ]);
                break;
              }

              case "tool_result": {
                const rawResult = data.result || "";
                const isToolError = rawResult.startsWith("Error:");
                const resultStatus = isToolError ? "error" : "done";
                const errorMsg = isToolError ? rawResult : undefined;
                const toolEntry = collectedTools.find(
                  (t) => t.name === data.name,
                );
                if (toolEntry) {
                  toolEntry.status = resultStatus;
                  toolEntry.result = rawResult;
                  if (errorMsg) toolEntry.errorMessage = errorMsg;
                }
                setToolActivity((prev) =>
                  prev.map((t) =>
                    t.name === data.name
                      ? {
                          ...t,
                          status: resultStatus,
                          result: rawResult,
                          errorMessage: errorMsg,
                        }
                      : t,
                  ),
                );
                break;
              }

              case "mcp_log": {
                const logMsg = sanitizeKaiStatusMessage(
                  data.message || "",
                  data.logger,
                );
                if (logMsg) setMcpStatus(logMsg);
                break;
              }

              case "plan_created": {
                if (data.plan) {
                  setMcpStatus("");
                  planProgressRef.current = data.plan;
                  setPlanProgress(data.plan);
                }
                break;
              }

              case "plan_step_start": {
                const stepId = data.step_id;
                if (stepId) {
                  const startStatus: KaiPlanStepStatus =
                    data.tool === "request_clarification"
                      ? "waiting"
                      : "running";
                  setPlanProgress((prev) => {
                    const next = prev
                      ? updatePlanStep(prev, stepId, startStatus)
                      : prev;
                    planProgressRef.current = next;
                    return next;
                  });
                }
                break;
              }

              case "plan_step_done": {
                const stepId = data.step_id;
                if (stepId) {
                  const status = (data.status || "ok") as KaiPlanStepStatus;
                  setPlanProgress((prev) => {
                    const next = prev
                      ? updatePlanStep(prev, stepId, status)
                      : prev;
                    planProgressRef.current = next;
                    return next;
                  });
                }
                break;
              }

              case "suggestions": {
                const items = data.suggestions ?? [];
                setFollowUpSuggestions(items.slice(0, 3));
                break;
              }

              case "clarification_request": {
                const clarId = data.clarification_id;
                if (clarId) {
                  setMcpStatus("");
                  setPendingClarifications((prev) => [
                    ...prev,
                    {
                      clarificationId: clarId,
                      title: data.title || "A few details to proceed",
                      fields: normalizeClarificationFields(data.fields),
                      status: "pending",
                    },
                  ]);
                }
                break;
              }

              case "elicitation_request": {
                const elicitId = data.elicitation_id;
                if (elicitId) {
                  setMcpStatus("");
                  setPendingElicitations((prev) => [
                    ...prev,
                    {
                      elicitationId: elicitId,
                      message: data.message || "",
                      schema: data.schema || {},
                      status: "pending",
                    },
                  ]);
                }
                break;
              }

              case "apply_preferences":
                applyKaiPreferences(data.preferences);
                break;

              case "apply_table_preferences":
                applyKaiTablePreferences(data.page, data.preferences);
                break;

              case "apply_table_filters":
                applyKaiTableFilters(data.page, data.filters);
                break;

              case "apply_feed_filters":
                applyKaiFeedFilters(data.page, data.filters);
                break;

              case "apply_home_layout_patch":
                applyKaiHomeLayout(data.patch);
                break;

              case "home_layout_review_request":
                setHomeLayoutReview({
                  summary: data.summary ?? "Kai updated your home layout.",
                  widget_count: data.widget_count ?? 0,
                });
                dispatchHomeLayoutReview({
                  summary: data.summary ?? "Kai updated your home layout.",
                  widget_count: data.widget_count ?? 0,
                });
                break;

              case "done": {
                const finalContent = data.content || accumulated;
                const finalPlan = planProgressRef.current;
                committed = true;
                setIsGenerating(false);
                resetLiveState();

                if (finalPlan) {
                  kaiApi
                    .getConversation(conversationId)
                    .then((conv) => {
                      if (stillCurrent()) hydrateConversation(conv);
                    })
                    .catch((err) =>
                      console.warn("Failed to reload plan turn", err),
                    );
                  kaiApi
                    .listConversations()
                    .then((list) => {
                      if (!stillCurrent()) return;
                      setConversations((prev) => {
                        const byId = new Map(prev.map((c) => [c.id, c]));
                        return list.map((s) => {
                          const existing = byId.get(s.id);
                          return existing
                            ? {
                                ...summaryToConversation(s),
                                messages: existing.messages,
                                title: s.title ?? existing.title,
                                preview:
                                  existing.preview ||
                                  previewFromLocalMessages(existing.messages) ||
                                  summaryToConversation(s).preview,
                              }
                            : summaryToConversation(s);
                        });
                      });
                    })
                    .catch(() => undefined);
                  break;
                }

                const now = new Date().toISOString();
                const syntheticTools: KaiMessage[] = collectedTools.map(
                  (tool, idx) => ({
                    id: `tool-${Date.now()}-${idx}`,
                    role: "tool",
                    content: tool.errorMessage ?? tool.result ?? tool.args ?? "",
                    createdAt: now,
                    toolName: tool.name,
                  }),
                );
                const assistantMsg: KaiMessage = {
                  id: newId("msg"),
                  role: "assistant",
                  content: finalContent,
                  createdAt: now,
                };
                setConversationMessages(conversationId, (msgs) => [
                  ...msgs,
                  ...syntheticTools,
                  assistantMsg,
                ]);
                break;
              }

              case "error":
                committed = true;
                setIsGenerating(false);
                resetLiveState();
                setError(
                  data.message || "An error occurred during generation",
                );
                break;

              default:
                break;
            }
          },
          controller.signal,
          attachmentIds,
          {
            route: routeRef.current,
            role: profileRef.current?.role ?? undefined,
            sales_person_link:
              profileRef.current?.sales_person_link ?? undefined,
            table_context: buildKaiTableContext(routeRef.current),
            filter_context: buildKaiFilterContext(routeRef.current),
            home_context: buildKaiHomeContext(routeRef.current),
            feed_context: buildKaiFeedContext(routeRef.current),
            booking_detail_context: buildKaiBookingDetailContext(
              routeRef.current,
            ),
            feed_event_context: feedEventFocusRef.current ?? undefined,
            mail_review_context: mailReviewFocusRef.current ?? undefined,
            entity_tags: entityTags.length > 0 ? entityTags : undefined,
            slash_command: slash
              ? { id: slash.id, args: slash.args }
              : undefined,
          },
        );
      } catch (err) {
        if (!stillCurrent()) return;
        if ((err as Error).name === "AbortError") {
          if (!committed && accumulated) {
            committed = true;
            setConversationMessages(conversationId, (msgs) => [
              ...msgs,
              {
                id: newId("msg"),
                role: "assistant",
                content: accumulated,
                createdAt: new Date().toISOString(),
              },
            ]);
          }
        } else {
          console.error("Kai streaming failed", err);
          if (!committed && !accumulated) {
            setError(
              "Kai is unavailable right now. Please make sure the Kai service is running and try again.",
            );
          }
        }
      } finally {
        if (stillCurrent()) {
          if (!committed && accumulated) {
            setConversationMessages(conversationId, (msgs) => [
              ...msgs,
              {
                id: newId("msg"),
                role: "assistant",
                content: accumulated,
                createdAt: new Date().toISOString(),
              },
            ]);
          }
          setIsGenerating(false);
          abortControllerRef.current = null;
          resetLiveState();
        }
      }
    },
    [
      applyKaiPreferences,
      applyKaiTablePreferences,
      applyKaiTableFilters,
      applyKaiFeedFilters,
      applyKaiHomeLayout,
      ensureConversation,
      hydrateConversation,
      isGenerating,
      patchConversation,
      resetLiveState,
      setConversationMessages,
    ],
  );

  const value = useMemo<KaiChatContextValue>(
    () => ({
      historyOpen,
      toggleHistory,
      closeHistory,
      conversations,
      activeId,
      isGenerating,
      isTyping: isGenerating,
      selectConversation,
      startNewChat,
      setFeedEventFocus,
      setMailReviewFocus,
      sendMessage,
      ensureConversation,
      stopGeneration,
      messages,
      thread,
      isWelcome,
      streamingContent,
      streamingArtifacts,
      toolActivity,
      mcpStatus,
      planProgress,
      followUpSuggestions,
      pendingClarifications,
      awaitingClarification,
      submitClarification,
      pendingElicitations,
      respondToElicitation,
      homeLayoutReview,
      dismissHomeLayoutReview,
      error,
      dismissError,
      messageFeedback,
      submitMessageFeedback,
    }),
    [
      historyOpen,
      toggleHistory,
      closeHistory,
      conversations,
      activeId,
      isGenerating,
      selectConversation,
      startNewChat,
      setFeedEventFocus,
      setMailReviewFocus,
      sendMessage,
      ensureConversation,
      stopGeneration,
      messages,
      thread,
      isWelcome,
      streamingContent,
      streamingArtifacts,
      toolActivity,
      mcpStatus,
      planProgress,
      followUpSuggestions,
      pendingClarifications,
      awaitingClarification,
      submitClarification,
      pendingElicitations,
      respondToElicitation,
      homeLayoutReview,
      dismissHomeLayoutReview,
      error,
      dismissError,
      messageFeedback,
      submitMessageFeedback,
    ],
  );

  return (
    <KaiChatContext.Provider value={value}>{children}</KaiChatContext.Provider>
  );
}

export function useKaiChat() {
  return useContext(KaiChatContext);
}

/** Only for IconRail — returns null outside Kai provider. */
export function useKaiChatOptional() {
  return useContext(KaiChatContext);
}
